import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';

import * as MaintenanceReducer from "@app/maintenance/store/maintenance.reducer"
import {
    AddTaskAction,
    DeleteTaskAction,
    LoadRolesAction,
    LoadTaskAction,
    UpdateTaskAction
} from '@app/maintenance/store/maintenance.actions';


import { Observable, Subscription } from 'rxjs';
import {
    DeleteActivityAction,
    LoadActivityAction,
    LoadTasksAction,
    UpdateActivityAction
} from '@app/maintenance/store/maintenance.actions';
import { ConfirmationModalComponent } from '@app/shared/components';
import { ActivityModalComponent } from '../../components/activity-modal/activity-modal.component';
import { RoleService } from '@app/core/services/role.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivityTask } from '@app/shared/models/activity-task.model';
import { TaskFormComponent } from '@app/maintenance/components/forms/task-form/task-form.component';
import { AppUtils } from '@app/shared/util/app.utils';
import { UserRole } from "@models/role.model";
import { PagedResponse } from "@models/response.model";
import { filter } from "rxjs/operators";
import { isEmpty } from "lodash";

@Component({
    selector: 'app-activity-details',
    templateUrl: './activity-details.component.html',
    styleUrls: ['./activity-details.component.scss']
})
export class ActivityDetailsComponent implements OnInit, OnDestroy {
    @ViewChild('removeActivity', {static: false}) removeActivityModal: ConfirmationModalComponent;
    @ViewChild('removeTask', {static: false}) removeTaskModal: ConfirmationModalComponent;
    @ViewChild(ActivityModalComponent, {static: false}) activityModal: ActivityModalComponent;
    @ViewChild(TaskFormComponent, {static: false}) taskForm: TaskFormComponent;

    activity: any;
    toBeDeletedActivity: any;
    toBeDeletedTask: any;
    toBeDuplicatedTask: any;
    selectedTask: ActivityTask;
    tasks = [];
    roles = [];
    isFormVisible = true;
    searchQuery = '';

    activity$: Observable<any>;
    updatedActivity$: Observable<any>;
    tasks$: Observable<any>;
    task$: Observable<any>;
    updatedTask$: Observable<any>;
    roles$: Observable<PagedResponse<UserRole>>;

    loading$: Observable<any>;
    loadingTasks$: Observable<any>;

    activitySubscription: Subscription;
    updatedActivitySubscription: Subscription;
    tasksSubscription: Subscription;
    taskSubscription: Subscription;
    updatedTaskSubscription: Subscription;
    rolesSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private store: Store<any>,
        private roleService: RoleService,
        private translate: TranslateService
    ) {
        this.activity$ = this.store.pipe(select(MaintenanceReducer.getActivity));
        this.updatedActivity$ = this.store.pipe(select(MaintenanceReducer.getUpdatedActivity));
        this.tasks$ = this.store.pipe(select(MaintenanceReducer.getTasks));
        this.task$ = this.store.pipe(select(MaintenanceReducer.getTask));
        this.updatedTask$ = store.pipe(select(MaintenanceReducer.getUpdatedTask));
        this.loading$ = store.pipe(select(MaintenanceReducer.isLoading));
        this.loadingTasks$ = store.pipe(select(MaintenanceReducer.isLoadingTasks));
        this.roles$ = store.pipe(select(MaintenanceReducer.getRoles), filter(roles => !isEmpty(roles)));

        this.activitySubscription = this.activity$.subscribe(activity => {
            if (activity) {
                this.activity = activity;
            }
        });

        this.updatedActivitySubscription = this.updatedActivity$.subscribe(activity => {
            if (activity) {
                this.activity = activity;

                if (this.selectedTask) {
                    setTimeout(() => {
                        if (this.taskForm) {
                            this.taskForm.init(this.selectedTask);
                        }
                    })
                }
            }
        });

        this.tasksSubscription = this.tasks$.subscribe(tasks => {
            if (tasks) {
                this.tasks = tasks.items;
            }
        });

        this.taskSubscription = this.task$.subscribe(task => {
            if (task) {
                if (!this.toBeDuplicatedTask) {
                    this.selectedTask = task;
                } else {
                    this.toBeDuplicatedTask = task;
                }
                this.isFormVisible = true;
                setTimeout(() => {
                    if (this.taskForm) {
                        this.taskForm.init(this.toBeDuplicatedTask
                            ? {...this.toBeDuplicatedTask, name: this.toBeDuplicatedTask.name + AppUtils.COPY_STRING, photos: []}
                            : this.selectedTask
                        );
                    }
                });
            }
        });

        this.updatedTaskSubscription = this.updatedTask$.subscribe(updatedTask => {
            if (updatedTask) {
                this.selectedTask = updatedTask;
                this.isFormVisible = true;
                setTimeout(() => {
                    if (this.taskForm) {
                        this.taskForm.init(this.selectedTask);
                    }
                });
            }
        })

        this.rolesSubscription = this.roles$.subscribe(roles => {
            this.roles = roles.items.map(role => new Object({
                id: role.id,
                icon: "fa fa-user",
                title: role.name,
                subtitle: this.translate.instant(
                    `admin.toolMaintenancePlans.activities.modal.${role.isOob ? 'implicitRole' : 'personalizedRole'}`
                ),
                showAssign: true
            }));
        })
    }

    ngOnInit() {
        this.activity = null;
        this.selectedTask = null;
        this.isFormVisible = true;
        this.route.paramMap.subscribe(params => {
            this.store.dispatch(new LoadActivityAction(parseInt(params.get('id'), 10)));
            this.store.dispatch(new LoadTasksAction({activityId: parseInt(params.get('id'), 10)}));
        });

        this.store.dispatch(new LoadRolesAction());
    }

    onSearch(event: string) {
        this.searchQuery = event;
        this.store.dispatch(new LoadTasksAction({searchTerm: event, activityId: this.activity.id}));
    }

    onSelectTask(event: ActivityTask) {
        if (!this.selectedTask || this.selectedTask.id !== event.id) {
            this.selectedTask = event;
            return this.store.dispatch(new LoadTaskAction(this.selectedTask.id));
        } else if (this.selectedTask || this.selectedTask.id === event.id) {
            this.resetForm();
        }
    }

    onAddTaskClick(event: any) {
        this.selectedTask = null;
        this.isFormVisible = true;
        setTimeout(() => {
            if (this.taskForm) {
                this.taskForm.initEmpty();
            }
        });
    }

    onCreate(event) {
        this.toBeDuplicatedTask = null;
        this.store.dispatch(new AddTaskAction(event, this.activity));
    }

    onClose() {
        this.resetForm();
    }

    onEditActivityClick(event: any) {
        this.activityModal.open(event, 'edit');
    }

    onDeleteActivityClick(event: any) {
        this.toBeDeletedActivity = event;
        this.removeActivityModal.open();
    }

    onConfirmDeleteActivity() {
        this.store.dispatch(new DeleteActivityAction(this.toBeDeletedActivity));
        this.removeActivityModal.close();
        this.router.navigate(['../../'], {relativeTo: this.route});
    }

    onUpdateTask(event: any) {
        this.store.dispatch(new UpdateTaskAction({id: this.selectedTask.id, ...event}, this.activity));
    }

    onDeleteTask(event: any) {
        this.toBeDeletedTask = event;
        this.removeTaskModal.open();
    }

    onConfirmDeleteTask() {
        this.store.dispatch(new DeleteTaskAction(this.toBeDeletedTask.id));
        this.removeTaskModal.close();
    }

    onDuplicateTask(event: any) {
        this.toBeDuplicatedTask = event;
        this.selectedTask = null;
        return this.store.dispatch(new LoadTaskAction(this.toBeDuplicatedTask.id));
    }

    onEdit(event: any) {
        this.store.dispatch(new UpdateActivityAction({id: this.activity.id, ...event}));
    }

    resetForm() {
        this.taskForm.initEmpty();
        this.selectedTask = null;
        this.isFormVisible = true;
    }

    get inUse() {
        // TODO: check if it is part of a plan once maintenance plans are added
        return false;
    }

    ngOnDestroy() {
        if (this.activitySubscription) {
            this.activitySubscription.unsubscribe();
        }

        if (this.updatedActivitySubscription) {
            this.updatedActivitySubscription.unsubscribe();
        }

        if (this.taskSubscription) {
            this.taskSubscription.unsubscribe();
        }

        if (this.tasksSubscription) {
            this.tasksSubscription.unsubscribe();
        }

        if (this.rolesSubscription) {
            this.rolesSubscription.unsubscribe();
        }
    }
}
